import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import Logo from "../components/Logo";
import LoadingOverlay from "../components/LoadingOverlay";
import LoadingScreen from "../components/LoadingScreen";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { AUTH } from "../constants/testIds";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [postLogin, setPostLogin] = useState(false);

  const onLogin = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const startedAt = Date.now();
    try {
      const r = await api.post("/auth/login", { email, password });
      login(r.data.token, r.data.user);
      toast.success("Welcome back, Alhamdulillah");
      setPostLogin(true);
      const target = r.data.user?.terms_accepted ? "/dashboard" : "/terms";
      const dwell = Math.max(0, 1000 - (Date.now() - startedAt));
      setTimeout(() => {
        // Hard-navigate so the React tree fully re-boots with the new JWT.
        window.location.href = target;
      }, dwell);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
      setBusy(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setGoogleBusy(true);
    const redirectUrl = window.location.origin + "/dashboard";
    // Defer redirect so React renders the LoadingOverlay before navigation tears down the page
    setTimeout(() => {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 80);
  };

  return (
    <div className="dnya-pattern" style={pageStyle}>
      {postLogin && <LoadingScreen title="Assalamu Alaikum" subtitle="Preparing your space, Insha'Allah" />}
      {googleBusy && <LoadingOverlay message="Signing you in Insha'Allah..." />}
      <form onSubmit={onLogin} style={cardStyle} className="dnya-reveal">
        <div style={{ display: "flex", justifyContent: "center" }}><Logo size={80} /></div>
        <h1 className="font-dnya" style={{ fontSize: 28, color: "var(--dnya-high-1)", textAlign: "center", marginTop: 18, marginBottom: 6 }}>
          Welcome Back
        </h1>
        <div style={{ textAlign: "center", color: "var(--dnya-high-2)", fontStyle: "italic", fontSize: 13, marginBottom: 22 }}>
          Access your spiritual sanctuary
        </div>

        <label style={labelStyle}>Email</label>
        <input
          data-testid={AUTH.loginEmail}
          type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={inputStyle}
        />
        <label style={{ ...labelStyle, marginTop: 14 }}>Password</label>
        <div style={{ position: "relative" }}>
          <input
            data-testid={AUTH.loginPassword}
            type={show ? "text" : "password"} required value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            style={inputStyle}
          />
          <button type="button" onClick={() => setShow(!show)} aria-label="toggle password"
            style={{ position: "absolute", right: 10, top: 10, background: "transparent", border: 0, color: "var(--dnya-high-1)", cursor: "pointer" }}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <button data-testid={AUTH.loginSubmit} type="submit" disabled={busy} style={{ ...primaryBtn, marginTop: 22 }}>
          {busy ? "Bismillah..." : "Login"}
        </button>

        <div style={dividerStyle}>OR CONTINUE WITH</div>

        <button type="button" data-testid={AUTH.loginGoogle} onClick={onGoogle} disabled={googleBusy} style={{ ...googleBtn, opacity: googleBusy ? 0.6 : 1, cursor: googleBusy ? "wait" : "pointer" }}>
          <span style={{ fontSize: 18 }}>G</span> {googleBusy ? "Bismillah..." : "Continue with Google"}
        </button>

        <div style={{ textAlign: "center", marginTop: 22, fontSize: 13, color: "var(--dnya-high-2)" }}>
          Don&apos;t have an account?{" "}
          <Link data-testid={AUTH.goToSignup} to="/signup" style={{ color: "var(--dnya-high-1)", fontWeight: 600 }}>
            Sign up for free
          </Link>
        </div>
      </form>
    </div>
  );
}

const pageStyle = { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "var(--dnya-bg)" };
const cardStyle = { width: "100%", maxWidth: 420, background: "var(--dnya-card)", border: `1px solid var(--dnya-mosaic-1)`, borderRadius: 16, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.35)" };
const labelStyle = { display: "block", fontSize: 12, color: "var(--dnya-high-1)", marginBottom: 6, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" };
const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid var(--dnya-border)`, background: "var(--dnya-input-bg)", color: "var(--dnya-text)", outline: "none", fontSize: 14 };
const primaryBtn = { width: "100%", padding: "12px", borderRadius: 10, background: "var(--dnya-mosaic-1)", color: "#ffffff", border: 0, fontSize: 15, fontWeight: 600, cursor: "pointer" };
const googleBtn = { width: "100%", padding: "11px", borderRadius: 10, background: "var(--dnya-bg-2)", color: "var(--dnya-text)", border: `1px solid var(--dnya-border)`, fontSize: 14, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 };
const dividerStyle = { textAlign: "center", margin: "18px 0", color: "var(--dnya-mosaic-2)", fontSize: 11, letterSpacing: "0.15em" };
